import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Linkedin, ShieldCheck, ExternalLink } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const Footer = () => {
  const { 
    companyInfo, 
    siteSettings, 
    getAccreditationsFor,
    phone, 
    email, 
    address,
    logo,
    companyName,
    loading 
  } = useSiteData();

  const footerAccreditations = getAccreditationsFor('footer');

  return (
    <footer className="bg-black text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Logo and Description */}
          <div>
            <img 
              src={logo} 
              alt={companyName}
              className="h-10 mb-4"
              style={{ height: `${siteSettings.logoSizeMobile || 40}px` }}
            />
            <p className="text-gray-400 mb-4 text-sm">
              Engineering & Design Consultancy
            </p>
            <p className="text-gray-500 text-xs mb-4">
              {companyInfo.tagline || 'Building our future by engineering projects with generational value'}
            </p>
            {/* Accreditations */}
            {footerAccreditations.length > 0 && (
              <div className="flex gap-3 flex-wrap">
                {footerAccreditations.map((acc) => (
                  <div key={acc.id} className="bg-gray-900 p-2 rounded-lg hover:bg-gray-800 transition-colors" title={acc.name}>
                    {acc.logo ? (
                      <img src={acc.logo} alt={acc.name} className="w-8 h-8 object-contain" />
                    ) : (
                      <ShieldCheck className="w-6 h-6 text-blue-500" />
                    )}
                  </div>
                ))}
              </div>
            )}
            {footerAccreditations.length === 0 && (
              <div className="flex gap-3">
                <div className="bg-gray-900 p-3 rounded-lg">
                  <ShieldCheck className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            )}
          </div>

          {/* Navigate */}
          <div>
            <h3 className="font-bold text-lg mb-4">Navigate</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm">
                  About
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/projects" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Projects
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Meet the Team
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-500" />
                <a href={`tel:${phone}`} className="text-sm hover:text-white transition-colors">
                  {phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-500" />
                <a href={`mailto:${email}`} className="text-sm hover:text-white transition-colors">
                  {email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                <span className="text-sm">{address}</span>
              </li>
            </ul>
          </div>

          {/* Company Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">Company Info</h3>
            <div className="text-gray-400 text-sm space-y-2">
              {companyInfo.founded && <p>Founded: {companyInfo.founded}</p>}
              {companyInfo.companyNumber && (
                <>
                  <p>Company Registration:</p>
                  <p className="text-gray-500">{companyInfo.companyNumber}</p>
                </>
              )}
              {companyInfo.vatNumber && (
                <>
                  <p>VAT Number:</p>
                  <p className="text-gray-500">{companyInfo.vatNumber}</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
            </div>
            <div className="flex gap-6 text-sm text-gray-500 items-center">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              {companyInfo.linkedinUrl && (
                <a href={companyInfo.linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
